import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, tap, map, forkJoin } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CurrencyFormatterService } from '../../../shared/services/currency-formatter.service';
import { CountryService } from './country.service';

@Injectable({
  providedIn: 'root',
})
export class ClientSetupService {
  private baseUrl = environment.baseUrl + environment.coreUrl;
  private tenantUrl = environment.baseUrl + environment.accountUrl;

  constructor(
    private http: HttpClient,
    private currencyFormatter: CurrencyFormatterService,
    private countryService: CountryService,
  ) {}

  saveClientSetup(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}franchisees/setup`, payload);
  }
  getClientData(): Observable<any> {
    return this.http.get(`${this.baseUrl}franchisees/setup`);
  }

  saveTransferSettings(payload: any): Observable<any> {
    return this.http.post(`${this.tenantUrl}tenant`, payload);
  }

  getTenantAndCountry(): Observable<any> {
    return this.http.get(`${this.baseUrl}franchisees/tenant-and-country`);
  }

  getTenantAndGateway(): Observable<any> {
    return this.http.get(`${this.tenantUrl}tenant/tenant-and-gateway`);
  }

  getGatewayById(id: string): Observable<any> {
    return this.http.get(`${this.tenantUrl}tenant/payment-gateway/${id}`);
  }

  // Cached observable — only one HTTP call is made regardless of how many
  // components call initializeCurrencyFormatter() (e.g. every ChartWrapperComponent).
  private currencyFormatter$: Observable<any> | null = null;

  /**
   * Initialize currency formatter with tenant data.
   * The HTTP call is made once; subsequent subscribers receive the cached result.
   */
  initializeCurrencyFormatter(): Observable<any> {
    if (!this.currencyFormatter$) {
      this.currencyFormatter$ = forkJoin({
        tenant: this.getTenantAndCountry(),
        countries: this.countryService.getCountries(),
      }).pipe(
        tap(({ tenant, countries }) => {
          this.currencyFormatter.setCurrencyConfig(tenant.data);
          const countryList = Array.isArray(countries)
            ? countries
            : Array.isArray(countries?.data)
              ? countries.data
              : [];
          this.currencyFormatter.registerSymbols(countryList);
        }),
        map(({ tenant }) => tenant.data),
        shareReplay(1),
      );
    }
    return this.currencyFormatter$;
  }

  /**
   * Drops the cached tenant-and-country response so the next
   * initializeCurrencyFormatter() call re-fetches instead of replaying stale
   * data (e.g. a phone-input flag/ISO still showing the tenant's old country
   * after Aquila Setup changes it). Call after a successful setup save.
   */
  resetCurrencyFormatterCache(): void {
    this.currencyFormatter$ = null;
  }
}
